import React, { useState, useRef } from 'react';
import './ProfileSetupModal.css';

const ProfileSetupModal = ({ onComplete }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [resumeFilename, setResumeFilename] = useState('');

  const [profile, setProfile] = useState({
    fullName: '', email: '', phone: '', location: '',
    linkedIn: '', github: '', portfolio: '',
    jobTitle: '', professionalSummary: '', skills: '',
    certifications: '',
    workExperience: [{ company: '', position: '', duration: '', description: '' }],
    education: [{ institution: '', degree: '', field: '', year: '' }],
    projects: [{ name: '', description: '', technologies: '' }]
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];
    if (!allowed.includes(file.type)) {
      setUploadError('Please upload a PDF or DOCX file');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    const formData = new FormData();
    formData.append('resume', file);

    try {
      const res = await fetch(`${API_BASE_URL}/api/profile/parse-resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Upload failed');

      setProfile(prev => ({
        ...prev,
        ...data.data,
        workExperience: data.data.workExperience?.length
          ? data.data.workExperience
          : [{ company: '', position: '', duration: '', description: '' }],
        education: data.data.education?.length
          ? data.data.education
          : [{ institution: '', degree: '', field: '', year: '' }],
        projects: data.data.projects?.length
          ? data.data.projects
          : [{ name: '', description: '', technologies: '' }]
      }));

      setResumeFilename(data.resumeFilename || '');

      setStep('review');
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!profile.fullName?.trim()) {
      setSaveError('Full name is required');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/profile/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ ...profile, resumeFilename })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Save failed');

      onComplete(profile);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field, value) => setProfile(p => ({ ...p, [field]: value }));

  const updateArrayItem = (arrayName, index, field, value) => {
    setProfile(p => {
      const arr = [...p[arrayName]];
      arr[index] = { ...arr[index], [field]: value };
      return { ...p, [arrayName]: arr };
    });
  };

  const addArrayItem = (arrayName, template) => {
    setProfile(p => ({ ...p, [arrayName]: [...p[arrayName], template] }));
  };

  const removeArrayItem = (arrayName, index) => {
    setProfile(p => ({
      ...p,
      [arrayName]: p[arrayName].filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="psm-overlay">
      <div className="psm-modal">

        {/* Header */}
        <div className="psm-header">
          <div className="psm-icon">👤</div>
          <h2 className="psm-title">Complete Your Profile</h2>
          <p className="psm-subtitle">
            {step === 'upload'
              ? "Upload your resume and we'll fill in your profile automatically"
              : 'Review your information and save your profile'}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="psm-steps">
          {['Upload Resume', 'Review & Save'].map((label, i) => (
            <React.Fragment key={i}>
              <div className={`psm-step ${
                (i === 0 && step === 'upload') || (i === 1 && step === 'review') ? 'psm-step--active' : ''
              }`}>
                <div className={`psm-step-circle ${
                  i === 0 && step === 'review' ? 'psm-step-circle--done' :
                  (i === 0 && step === 'upload') || (i === 1 && step === 'review') ? 'psm-step-circle--active' : ''
                }`}>
                  {i === 0 && step === 'review' ? '✓' : i + 1}
                </div>
                <span className="psm-step-label">{label}</span>
              </div>
              {i === 0 && <div className="psm-step-divider" />}
            </React.Fragment>
          ))}
        </div>

        {/* ── STEP 1: Upload ── */}
        {step === 'upload' && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.doc"
              onChange={handleFileUpload}
              className="psm-file-input"
            />

            <div
              className={`psm-dropzone ${isUploading ? 'psm-dropzone--uploading' : ''}`}
              onClick={() => !isUploading && fileInputRef.current?.click()}
              onMouseEnter={e => { if (!isUploading) e.currentTarget.classList.add('psm-dropzone--hover'); }}
              onMouseLeave={e => e.currentTarget.classList.remove('psm-dropzone--hover')}
            >
              {isUploading ? (
                <>
                  <div className="psm-dropzone-icon">⚙️</div>
                  <p className="psm-dropzone-title psm-dropzone-title--loading">
                    Parsing your resume with AI...
                  </p>
                  <p className="psm-dropzone-sub">This takes about 10–15 seconds</p>
                </>
              ) : (
                <>
                  <div className="psm-dropzone-icon">📄</div>
                  <p className="psm-dropzone-title">Click to upload your resume</p>
                  <p className="psm-dropzone-sub">PDF or DOCX supported • Max 10MB</p>
                </>
              )}
            </div>

            {uploadError && (
              <div className="psm-error-box">⚠️ {uploadError}</div>
            )}
          </div>
        )}

        {/* ── STEP 2: Review ── */}
        {step === 'review' && (
          <div>
            <Section title="Personal Information">
              <Grid>
                <Field label="Full Name *" value={profile.fullName} onChange={v => updateField('fullName', v)} />
                <Field label="Email" value={profile.email} onChange={v => updateField('email', v)} />
                <Field label="Phone" value={profile.phone} onChange={v => updateField('phone', v)} />
                <Field label="Location" value={profile.location} onChange={v => updateField('location', v)} />
                <Field label="Job Title" value={profile.jobTitle} onChange={v => updateField('jobTitle', v)} />
                <Field label="LinkedIn URL" value={profile.linkedIn} onChange={v => updateField('linkedIn', v)} />
                <Field label="GitHub URL" value={profile.github} onChange={v => updateField('github', v)} />
                <Field label="Portfolio URL" value={profile.portfolio} onChange={v => updateField('portfolio', v)} />
              </Grid>
            </Section>

            <Section title="Professional Summary">
              <TextArea value={profile.professionalSummary} onChange={v => updateField('professionalSummary', v)} rows={4} placeholder="Brief professional summary..." />
            </Section>

            <Section title="Skills">
              <TextArea value={profile.skills} onChange={v => updateField('skills', v)} rows={3} placeholder="e.g. JavaScript, React, Node.js, Python..." />
            </Section>

            <Section
              title="Work Experience"
              action={<AddBtn onClick={() => addArrayItem('workExperience', { company: '', position: '', duration: '', description: '' })} />}
            >
              {profile.workExperience.map((exp, i) => (
                <SubItem key={i} label={`Experience #${i + 1}`}
                  onRemove={profile.workExperience.length > 1 ? () => removeArrayItem('workExperience', i) : null}>
                  <Grid>
                    <Field label="Company" value={exp.company} onChange={v => updateArrayItem('workExperience', i, 'company', v)} />
                    <Field label="Position" value={exp.position} onChange={v => updateArrayItem('workExperience', i, 'position', v)} />
                    <Field label="Duration" value={exp.duration} onChange={v => updateArrayItem('workExperience', i, 'duration', v)} placeholder="e.g. Jan 2022 – Present" />
                  </Grid>
                  <TextArea value={exp.description} rows={3} onChange={v => updateArrayItem('workExperience', i, 'description', v)} placeholder="Key responsibilities and achievements..." />
                </SubItem>
              ))}
            </Section>

            <Section
              title="Education"
              action={<AddBtn onClick={() => addArrayItem('education', { institution: '', degree: '', field: '', year: '' })} />}
            >
              {profile.education.map((edu, i) => (
                <SubItem key={i} label={`Education #${i + 1}`}
                  onRemove={profile.education.length > 1 ? () => removeArrayItem('education', i) : null}>
                  <Grid>
                    <Field label="Institution" value={edu.institution} onChange={v => updateArrayItem('education', i, 'institution', v)} />
                    <Field label="Degree" value={edu.degree} onChange={v => updateArrayItem('education', i, 'degree', v)} />
                    <Field label="Field of Study" value={edu.field} onChange={v => updateArrayItem('education', i, 'field', v)} />
                    <Field label="Year" value={edu.year} onChange={v => updateArrayItem('education', i, 'year', v)} placeholder="e.g. 2018 – 2022" />
                  </Grid>
                </SubItem>
              ))}
            </Section>

            <Section
              title="Projects"
              action={<AddBtn onClick={() => addArrayItem('projects', { name: '', description: '', technologies: '' })} />}
            >
              {profile.projects.map((proj, i) => (
                <SubItem key={i} label={`Project #${i + 1}`}
                  onRemove={profile.projects.length > 1 ? () => removeArrayItem('projects', i) : null}>
                  <Grid>
                    <Field label="Project Name" value={proj.name} onChange={v => updateArrayItem('projects', i, 'name', v)} />
                    <Field label="Technologies" value={proj.technologies} onChange={v => updateArrayItem('projects', i, 'technologies', v)} />
                  </Grid>
                  <TextArea value={proj.description} rows={3} onChange={v => updateArrayItem('projects', i, 'description', v)} placeholder="What you built and the impact..." />
                </SubItem>
              ))}
            </Section>

            <Section title="Certifications">
              <TextArea value={profile.certifications} onChange={v => updateField('certifications', v)} rows={2} placeholder="e.g. AWS Certified Developer, Google Analytics..." />
            </Section>

            {saveError && (
              <div className="psm-error-box psm-error-box--bottom">⚠️ {saveError}</div>
            )}

            <div className="psm-actions">
              <button className="psm-btn-back" onClick={() => setStep('upload')}>
                ← Re-upload
              </button>
              <button
                className={`psm-btn-save ${isSaving ? 'psm-btn-save--disabled' : ''}`}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Profile & Continue'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

// ── Helper Components ────────────────────────────────────────────────────────

const Section = ({ title, children, action }) => (
  <div className="psm-section">
    <div className="psm-section-header">
      <h3 className="psm-section-title">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div className="psm-grid">{children}</div>
);

const Field = ({ label, value, onChange, placeholder }) => (
  <div className="psm-field">
    <label className="psm-field-label">{label}</label>
    <input
      type="text"
      value={value || ''}
      placeholder={placeholder || ''}
      onChange={e => onChange(e.target.value)}
      className="psm-input"
    />
  </div>
);

const TextArea = ({ value, onChange, rows, placeholder }) => (
  <textarea
    value={value || ''}
    rows={rows || 3}
    placeholder={placeholder || ''}
    onChange={e => onChange(e.target.value)}
    className="psm-textarea"
  />
);

const SubItem = ({ label, children, onRemove }) => (
  <div className="psm-subitem">
    <div className="psm-subitem-header">
      <span className="psm-subitem-label">{label}</span>
      {onRemove && (
        <button className="psm-btn-remove" onClick={onRemove}>Remove</button>
      )}
    </div>
    {children}
  </div>
);

const AddBtn = ({ onClick }) => (
  <button className="psm-btn-add" onClick={onClick}>+ Add</button>
);

export default ProfileSetupModal;